import { useEffect, useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Mail, Lock } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { AuthOtpInput } from "@features/auth/components/AuthOtpInput";
import BookingHeader from "@features/booking/components/BookingHeader";
import { BookingChrome } from "@features/booking/layouts";
import BookingFooter from "@features/booking/components/BookingFooter";
import { toast } from "sonner";
import { useAuth, redirectIfProfileIncomplete } from "@core/auth";
import { loginWithPassword } from "@core/auth/authApi";
import { ApiError } from "@core/api";
import { useTenant } from "@core/tenant";
import { useBookingFlow } from "@features/booking/flow";
import { useBooking } from "@features/booking/state";
import { usePasswordlessAuth } from "../hooks/usePasswordlessAuth";

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");
  const { loginWithToken, authState } = useAuth();
  const { subdomainPrefix } = useTenant();
  const { activeSteps } = useBookingFlow();
  const { selectedDate, selectedTime } = useBooking();

  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const [searchParams] = useSearchParams();
  // AuthReturnGate redirects users here with ?auth_error=storage_write_failed
  // when the cross-subdomain login could not persist its token (in-app browser,
  // private mode, full storage). Read it on first render so the banner stays
  // visible after AuthErrorToast strips the param from the URL.
  const fallbackBanner = useMemo(
    () => searchParams.get('auth_error') === 'storage_write_failed',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Post-auth redirect:
  //   1. explicit returnTo → honor it
  //   2. user is mid-booking (date + time picked, sessionStorage rehydrated)
  //      → continue to the next step after SIGN_IN
  //   3. neither → home, because the user landed on /sign-in directly and
  //      has no booking context to resume into
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    // Compute the natural post-login destination first so it can be passed
    // as `from` if we have to detour through profile completion.
    let target: string;
    if (returnTo) {
      target = returnTo;
    } else if (selectedDate && selectedTime) {
      // Skip auth-related steps (SIGN_IN, COMPLETE_PROFILE) — the profile
      // gate below handles the detour and uses this target as `from`.
      const timeIdx = activeSteps.findIndex(s => s.id === 'TIME');
      const nextStep = activeSteps
        .slice(timeIdx + 1)
        .find(s => s.id !== 'SIGN_IN' && s.id !== 'COMPLETE_PROFILE');
      target = nextStep?.path || '/personalize';
    } else {
      target = '/';
    }

    // Profile completion gate (legacy parity): force user to complete
    // name + phone before any post-login redirect. The global enforcer
    // would catch this on the next route change, but short-circuiting
    // here avoids a visible flicker through the target route.
    if (redirectIfProfileIncomplete(navigate, authState, target)) return;
    navigate(target, { replace: true });
  }, [authState, activeSteps, returnTo, selectedDate, selectedTime, navigate]);

  const handleSuccess = useCallback(
    async (token: string, email: string, flowId: string) => {
      await loginWithToken(token, email, flowId);
      toast.success(t("MAGIC_LINK_CODE_SENT"));
    },
    [loginWithToken, t],
  );

  const pw = usePasswordlessAuth({
    restaurant: subdomainPrefix || "",
    onSuccess: handleSuccess,
  });

  // Password login state
  const [passwordStep, setPasswordStep] = useState(false);
  const [pwEmail, setPwEmail] = useState("");
  const [pwPassword, setPwPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");

  const handlePasswordLogin = useCallback(async () => {
    setPwError("");
    setPwLoading(true);
    try {
      const res = await loginWithPassword({ username: pwEmail, password: pwPassword });
      await loginWithToken(res.token, pwEmail);
    } catch (err: unknown) {
      // Only surface ApiError.message (backend-provided, user-facing). Errors
      // from loginWithToken like 'auth.token_write_readback_mismatch' are
      // internal identifiers and must not reach the UI.
      const msg = err instanceof ApiError ? err.message : "";
      setPwError(msg || t("MAGIC_LINK_GENERIC_ERROR"));
    } finally {
      setPwLoading(false);
    }
  }, [pwEmail, pwPassword, loginWithToken, t]);

  const handleBack = useCallback(() => {
    if (passwordStep) {
      setPasswordStep(false);
      setPwEmail("");
      setPwPassword("");
      setPwError("");
      return;
    }
    if (pw.step === "initial") {
      navigate(-1);
    } else {
      pw.goBack();
    }
  }, [passwordStep, pw, navigate]);

  const handleSignUp = () => {
    navigate("/sign-up", { state: { returnTo } });
  };

  const renderInitialStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t("SIGNIN_BOOKING_TITLE")}
      </h1>
      <p className="text-foreground/50 text-sm mb-8 text-center max-w-xs">
        {t("SIGNIN_BOOKING_SUBTITLE")}
      </p>

      <div className="w-full max-w-xs flex flex-col gap-3 mb-10">
        {/* Email (passwordless OTP) — primary path */}
        <Button
          className="py-6 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02]"
          onClick={pw.goToEmail}
          type="button"
          aria-label={t("SIGNIN_CONTINUE_WITH_EMAIL")}
        >
          <Mail className="w-5 h-5 mr-2" />
          {t("SIGNIN_CONTINUE_WITH_EMAIL")}
        </Button>
      </div>

      <div className="w-full text-center">
        <button
          className="text-foreground/50 text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
          onClick={() => setPasswordStep(true)}
          type="button"
        >
          <Lock className="w-3.5 h-3.5 inline mr-1" />
          {t("LOGIN_WITH_PASSWORD")}
        </button>
      </div>
    </>
  );

  const renderEmailStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t("MAGIC_LINK_TITLE")}
      </h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">
        {t("SIGNIN_SOCIAL_SUBTITLE")}
      </p>

      <form
        className="w-full max-w-xs flex flex-col gap-4 mb-10"
        onSubmit={(e) => {
          e.preventDefault();
          pw.sendCode();
        }}
      >
        <Input
          type="email"
          placeholder={t("MAGIC_LINK_EMAIL_PLACEHOLDER")}
          value={pw.email}
          onChange={(e) => pw.setEmail(e.target.value)}
          autoFocus
          aria-label={t("SIGNUP_LABEL_EMAIL")}
          className="py-6"
        />

        {pw.error && (
          <p className="text-red-500 text-sm text-center">{pw.error}</p>
        )}

        <Button
          type="submit"
          className="py-6 transition-all duration-200 hover:scale-[1.02]"
          disabled={pw.isSending || !pw.email}
        >
          {pw.isSending ? t("MAGIC_LINK_SENDING") : t("MAGIC_LINK_SEND_CODE")}
        </Button>
      </form>
    </>
  );

  const renderVerificationStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t("MAGIC_LINK_CHECK_EMAIL")}
      </h1>
      <p className="text-foreground/50 text-sm mb-6 text-center">
        {t("MAGIC_LINK_VERIFY_SUBTITLE")}
      </p>
      <p className="text-foreground font-medium text-sm mb-8 text-center">
        {pw.email}
      </p>

      <div className="w-full max-w-xs flex flex-col items-center gap-4 mb-10">
        <AuthOtpInput
          value={pw.otp}
          onChange={pw.handleOtpChange}
          disabled={pw.isVerifying}
        />

        {/* Fixed-height status row prevents layout shift when "Verifying..."
            or an error toggles in/out of the DOM. */}
        <div className="min-h-5 flex items-center" aria-live="polite">
          {pw.isVerifying && (
            <p className="text-foreground/50 text-sm">{t("MAGIC_LINK_VERIFY_BUTTON")}</p>
          )}
          {pw.error && (
            <p className="text-red-500 text-sm text-center">{pw.error}</p>
          )}
        </div>

        <div className="flex flex-col items-center gap-2 mt-4">
          <p className="text-foreground/50 text-sm">
            {t("MAGIC_LINK_DIDNT_RECEIVE")}
          </p>
          <button
            type="button"
            className="text-brand font-medium text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={pw.resendCode}
            disabled={pw.resendCooldown > 0 || pw.isSending}
          >
            {pw.resendCooldown > 0
              ? t("MAGIC_LINK_RESEND_IN", { seconds: pw.resendCooldown })
              : t("MAGIC_LINK_RESEND_CODE")}
          </button>
          <button
            type="button"
            className="text-foreground/50 text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
            onClick={() => pw.goBack()}
          >
            {t("MAGIC_LINK_CHANGE_EMAIL")}
          </button>
        </div>
      </div>
    </>
  );

  const renderPasswordStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t("SIGNIN_TITLE")}
      </h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">
        {t("SIGNIN_PASSWORD_SUBTITLE")}
      </p>

      <form
        className="w-full max-w-xs flex flex-col gap-4 mb-10"
        onSubmit={(e) => {
          e.preventDefault();
          handlePasswordLogin();
        }}
      >
        <Input
          type="email"
          placeholder={t("SIGNUP_LABEL_EMAIL")}
          value={pwEmail}
          onChange={(e) => setPwEmail(e.target.value)}
          autoFocus
          aria-label={t("SIGNUP_LABEL_EMAIL")}
          className="py-6"
        />
        <Input
          type="password"
          placeholder={t("FORM_LABEL_PASSWORD")}
          value={pwPassword}
          onChange={(e) => setPwPassword(e.target.value)}
          aria-label={t("FORM_LABEL_PASSWORD")}
          className="py-6"
        />

        {pwError && (
          <p className="text-red-500 text-sm text-center">{pwError}</p>
        )}

        <Button
          type="submit"
          className="py-6 transition-all duration-200 hover:scale-[1.02]"
          disabled={pwLoading || !pwEmail || !pwPassword}
        >
          {pwLoading ? t("SIGNIN_SIGNING_IN") : t("SIGNIN_TITLE")}
        </Button>
      </form>
    </>
  );

  return (
    <BookingChrome>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center">
          {fallbackBanner && (
            <div
              role="status"
              className="w-full max-w-xs mb-6 rounded-md border border-brand/40 bg-brand/5 px-4 py-3 text-sm text-foreground/80"
            >
              {t("AUTH_FALLBACK_AFTER_FAILURE")}
            </div>
          )}
          {passwordStep && renderPasswordStep()}
          {!passwordStep && pw.step === "initial" && renderInitialStep()}
          {!passwordStep && pw.step === "email" && renderEmailStep()}
          {!passwordStep && pw.step === "verification" && renderVerificationStep()}
        </main>

        <footer className="border-t border-border">
          {/* Hide back row on the initial step — its only destination is the
              prior booking step (e.g. time selection) which is rarely what
              the user wants from here, and removing it reinforces the
              "finish your booking" framing. Keep it on the email and
              verification sub-steps where back means "change my email". */}
          {(passwordStep || pw.step !== "initial") && (
            <div className="flex items-center px-6 py-4">
              <button
                aria-label="Go back to previous page"
                className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm mr-4"
                onClick={handleBack}
                type="button"
              >
                <ChevronLeft
                  size={20}
                  className="group-hover:scale-110 transition-transform"
                />
              </button>
            </div>
          )}
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default SignIn;
